import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';

import requestAccessRecovery from '../api/login/requestAccessRecovery';
import { validateNif, validateEmail } from '../utils/employeeInviteValidation';
import { I18nContext } from '../context/translations/TranslationsContext.context';

import SubmissionResultModalV2 from '../components/Modals/SubmissionResultModalV2';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '../components/ui/card';

interface FormErrors {
	nif?: string;
	birthdate?: string;
	contactEmail?: string;
	phone?: string;
}

type ResultModalState = { variant: 'success' | 'error' } | null;

const validatePhone = (value: string): boolean => {
	const digitsOnly = value.replace(/[\s-]/g, '');
	return /^\+?\d{9,15}$/.test(digitsOnly);
};

export interface RecoverAccessV2Props {
	entryContext?: 'default' | 'offboarded';
}

// `location.state` es cómo llega aquí el caso esquina de LoginFormV2 (NIF encontrado
// sin correo al que entregar el enlace): trae el NIF ya escrito, para no hacer que lo
// repita, y `entryContext: 'offboarded'` para mostrar el mismo subtítulo que ese caso.
// El prop `entryContext` (para cuando algún día se enlace aquí desde otra pantalla)
// gana si se pasa explícitamente.
type RecoverAccessNavigationState = {
	nif?: string;
	entryContext?: 'default' | 'offboarded';
} | null;

const RecoverAccessV2 = ({ entryContext: entryContextProp }: RecoverAccessV2Props) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const navigate = useNavigate();
	const location = useLocation();
	const navigationState = location.state as RecoverAccessNavigationState;
	const entryContext = entryContextProp ?? navigationState?.entryContext ?? 'default';

	// Cuando el NIF llega ya resuelto desde el caso esquina de LoginFormV2, se muestra
	// como dato fijo (no editable) en vez de como campo — ya lo confirmó el backend,
	// pedírselo de nuevo no aporta nada y solo genera dudas.
	const hasKnownNif = Boolean(navigationState?.nif);
	const [nif, setNif] = useState(navigationState?.nif ?? '');
	const [birthdate, setBirthdate] = useState('');
	const [contactEmail, setContactEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [resultModal, setResultModal] = useState<ResultModalState>(null);

	const validate = (): boolean => {
		const newErrors: FormErrors = {};

		if (!nif.trim()) {
			newErrors.nif = translate('general.recoverAccessForm.nifRequiredError');
		} else if (!validateNif(nif)) {
			newErrors.nif = translate('general.recoverAccessForm.nifInvalidError');
		}

		if (!birthdate) {
			newErrors.birthdate = translate('general.recoverAccessForm.birthdateRequiredError');
		} else if (new Date(birthdate) > new Date()) {
			newErrors.birthdate = translate('general.recoverAccessForm.birthdateFutureError');
		}

		if (!contactEmail.trim()) {
			newErrors.contactEmail = translate('general.recoverAccessForm.contactEmailRequiredError');
		} else if (!validateEmail(contactEmail)) {
			newErrors.contactEmail = translate('general.recoverAccessForm.contactEmailInvalidError');
		}

		if (phone.trim() && !validatePhone(phone)) {
			newErrors.phone = translate('general.recoverAccessForm.phoneInvalidError');
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!validate()) {
			return;
		}

		setIsSubmitting(true);
		try {
			// Anti-oráculo (mismo principio que el enlace de recuperación): el backend
			// responde igual exista o no el NIF, para no confirmar ni desmentir que hay
			// cuenta. `success: false` queda para errores de verdad.
			// El tope de solicitudes lo pone el backend (3 al día por correo de contacto);
			// el captcha sigue pendiente de decidir proveedor.
			const response = await requestAccessRecovery({
				nif,
				birthdate,
				contactEmail,
				phone: phone.trim() || undefined
			});
			setResultModal({ variant: response.success ? 'success' : 'error' });
		} catch (error) {
			console.error('Error al enviar la solicitud de recuperación de acceso:', error);
			setResultModal({ variant: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleResultModalPrimaryAction = () => {
		if (resultModal?.variant === 'success') {
			navigate('/login');
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center space-y-4">
					<div className="flex justify-center">
						<img src="/imgs/LOGO_VERDE_2.svg" alt="Logo Arca" className="h-12" />
					</div>
					<CardTitle className="text-2xl font-semibold text-gray-900">
						{translate('general.login.recoverAccessTitle')}
					</CardTitle>
					<CardDescription className="text-gray-600">
						{entryContext === 'offboarded'
							? translate('general.recoverAccessForm.offboardedSubtitle')
							: translate('general.recoverAccessForm.subtitle')}
					</CardDescription>
					<button
						type="button"
						onClick={() => navigate('/login')}
						className="inline-flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-arca-dark-green transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
						{translate('general.login.backToPasswordLogin')}
					</button>
				</CardHeader>

				{/* `noValidate`: la validación es nuestra (`validate()`), con los mensajes de la pantalla.
				    Sin esto el navegador bloquea el envío con su aviso nativo y no se ven. */}
				<form noValidate onSubmit={handleSubmit}>
					<CardContent className="space-y-4">
						{hasKnownNif ? (
							<div className="rounded-md bg-gray-100 p-3 text-sm text-gray-700 space-y-1">
								<p>
									<span className="font-medium text-gray-900">
										{translate('general.recoverAccessForm.nifLabel')}:
									</span>{' '}
									{nif}
								</p>
								<p>{translate('general.recoverAccessForm.identityVerificationNotice')}</p>
							</div>
						) : (
							<div className="space-y-2">
								<Label htmlFor="recover-access-nif" className="text-sm font-medium text-gray-700">
									{translate('general.recoverAccessForm.nifLabel')}
								</Label>
								<Input
									id="recover-access-nif"
									name="nif"
									type="text"
									value={nif}
									onChange={(e) => setNif(e.target.value)}
									placeholder={translate('general.recoverAccessForm.nifLabel')}
									className={`h-11 ${errors.nif ? 'border-red-500' : ''}`}
								/>
								{errors.nif && <p className="text-sm text-red-500">{errors.nif}</p>}
							</div>
						)}

						<div className="space-y-2">
							<Label
								htmlFor="recover-access-birthdate"
								className="text-sm font-medium text-gray-700"
							>
								{translate('general.recoverAccessForm.birthdateLabel')}
							</Label>
							<Input
								id="recover-access-birthdate"
								name="birthdate"
								type="date"
								value={birthdate}
								onChange={(e) => setBirthdate(e.target.value)}
								className={`h-11 ${errors.birthdate ? 'border-red-500' : ''}`}
							/>
							{errors.birthdate && <p className="text-sm text-red-500">{errors.birthdate}</p>}
						</div>

						<div className="space-y-2">
							<Label
								htmlFor="recover-access-contact-email"
								className="text-sm font-medium text-gray-700"
							>
								{translate('general.recoverAccessForm.contactEmailLabel')}
							</Label>
							<Input
								id="recover-access-contact-email"
								name="contactEmail"
								type="email"
								value={contactEmail}
								onChange={(e) => setContactEmail(e.target.value)}
								placeholder={translate('general.recoverAccessForm.contactEmailLabel')}
								className={`h-11 ${errors.contactEmail ? 'border-red-500' : ''}`}
							/>
							{errors.contactEmail && <p className="text-sm text-red-500">{errors.contactEmail}</p>}
						</div>

						<div className="space-y-2">
							<Label htmlFor="recover-access-phone" className="text-sm font-medium text-gray-700">
								{translate('general.recoverAccessForm.phoneLabel')}
							</Label>
							<Input
								id="recover-access-phone"
								name="phone"
								type="tel"
								value={phone}
								onChange={(e) => setPhone(e.target.value)}
								placeholder={translate('general.recoverAccessForm.phoneLabel')}
								className={`h-11 ${errors.phone ? 'border-red-500' : ''}`}
							/>
							{errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
						</div>
					</CardContent>

					<CardFooter className="flex flex-col gap-4">
						<Button
							type="submit"
							className="w-full h-11 bg-arca-dark-green hover:bg-arca-dark-green/90 text-white font-medium"
							disabled={isSubmitting}
						>
							{isSubmitting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{translate('general.recoverAccessForm.submitButtonLoading')}
								</>
							) : (
								translate('general.recoverAccessForm.submitButton')
							)}
						</Button>
					</CardFooter>
				</form>
			</Card>

			<SubmissionResultModalV2
				open={resultModal !== null}
				// El modal no tiene disparador propio: solo se abre desde `setResultModal`, así
				// que `onOpenChange` únicamente llega al cerrar (botón, Escape o clic fuera).
				// Elena traía aquí un `if (!open)`; esa rama nunca se ejecuta.
				onOpenChange={() => setResultModal(null)}
				variant={resultModal?.variant ?? 'success'}
				title={
					resultModal?.variant === 'error'
						? translate('general.recoverAccessForm.errorTitle')
						: translate('general.recoverAccessForm.submittedTitle')
				}
				message={
					resultModal?.variant === 'error'
						? translate('general.recoverAccessForm.errorMessage')
						: translate('general.recoverAccessForm.submittedMessage')
				}
				primaryActionLabel={
					resultModal?.variant === 'error'
						? translate('general.genericModal.accept')
						: translate('general.login.backToPasswordLogin')
				}
				onPrimaryAction={handleResultModalPrimaryAction}
			/>
		</div>
	);
};

export default RecoverAccessV2;
